import type {
    LiceReportClientLocation,
    LiceReportResult,
    LiceReportWithTotals,
} from '@/routes/Client/LiceReport/types';
import { isChileanLiceType } from '@/utils/locationTypeHelpers';

type CageWithToggle = { cageId: number; isIncluded?: boolean };
type LocationWithCages = { id: number; cages: CageWithToggle[] };
type ReportWithCageToggles = {
    isoWeek: number;
    isoYear: number;
    locations: LocationWithCages[];
};

export type BuildLiceReportSelectionMapParams = {
    reports: LiceReportResult[];
    clientLocations: LiceReportClientLocation[];
    locationId?: number;
};

/**
 * Builds the week/day key → report map used by lice report selection.
 * When locationId is set, only that location is kept on each report.
 */
export function buildLiceReportSelectionMap({
    reports,
    clientLocations,
    locationId,
}: BuildLiceReportSelectionMapParams): {
    map: Map<string, LiceReportWithTotals>;
    keysInOrder: string[];
} {
    const map = new Map<string, LiceReportWithTotals>();
    const keysInOrder: string[] = [];

    for (const report of reports) {
        const locations =
            locationId === undefined
                ? report.locations
                : report.locations.filter((location) => location.id === locationId);

        if (locationId !== undefined && locations.length === 0) {
            continue;
        }

        const key = getLiceReportMapKey(report);
        keysInOrder.push(key);
        map.set(key, {
            ...report,
            locations: locations.map((location) => ({
                ...location,
                cages: location.cages.map((cage) => ({ ...cage })),
                isChileanLice: isChileanLiceType(
                    clientLocations.find((l) => l.id === location.id)?.lice_type
                ),
            })),
        });
    }

    return { map, keysInOrder };
}

export function toggleCageIncludedInReports<T extends ReportWithCageToggles>(
    reports: T[],
    reportIndex: number,
    locationId: number,
    cageId: number
): T[] {
    return reports.map((report, currentReportIndex) => {
        if (currentReportIndex !== reportIndex) {
            return report;
        }

        return {
            ...report,
            locations: report.locations.map((location) => {
                if (location.id !== locationId) {
                    return location;
                }

                return {
                    ...location,
                    cages: location.cages.map((cage) => {
                        if (cage.cageId !== cageId) {
                            return cage;
                        }
                        return {
                            ...cage,
                            isIncluded: !cage.isIncluded,
                        };
                    }),
                };
            }),
        };
    });
}

/**
 * Carry a cage toggle onto a daily or range report only when it was made for that same
 * period. Weekly merge matches `isoWeek`+`isoYear` alone, so an exclusion on Monday would
 * otherwise stay applied on Tuesday of the same week. A period with no prior toggle is
 * returned as loaded, cages included.
 */
export function applySamePeriodCageToggles<
    T extends ReportWithCageToggles & LiceReportMapKeySource,
>(report: T, previous: readonly T[]): T {
    const key = getLiceReportMapKey(report);
    const match = previous.find((item) => getLiceReportMapKey(item) === key);
    if (!match) {
        return report;
    }
    return mergeWeeklyReportCageToggles([report], [match])[0] ?? report;
}

/**
 * After resolving weekly selected reports from the map, carry over any cage
 * isIncluded toggles the user made in the previous selection.
 * Matching is by isoWeek+isoYear → locationId → cageId.
 */
export function mergeWeeklyReportCageToggles<T extends ReportWithCageToggles>(
    resolved: T[],
    prev: T[]
): T[] {
    if (prev.length === 0) return resolved;

    const prevByKey = new Map(prev.map((r) => [`${r.isoWeek}-${r.isoYear}`, r]));

    return resolved.map((report) => {
        const prevReport = prevByKey.get(`${report.isoWeek}-${report.isoYear}`);
        if (!prevReport) return report;

        return {
            ...report,
            locations: report.locations.map((location) => {
                const prevLocation = prevReport.locations.find((l) => l.id === location.id);
                if (!prevLocation) return location;

                return {
                    ...location,
                    cages: location.cages.map((cage) => {
                        const prevCage = prevLocation.cages.find((c) => c.cageId === cage.cageId);
                        if (!prevCage || prevCage.isIncluded === undefined) return cage;
                        return { ...cage, isIncluded: prevCage.isIncluded };
                    }),
                };
            }),
        };
    });
}

export type LiceReportMapKeySource = {
    day?: string;
    from?: string;
    to?: string;
    isoWeek: number;
    isoYear: number;
};

export function getLiceReportMapKey(report: LiceReportMapKeySource): string {
    if (report.from && report.to) {
        return `${report.from}-${report.to}`;
    }
    if (report.day) {
        return report.day;
    }
    return `${report.isoWeek}-${report.isoYear}`;
}

export function isWeeklyLiceReport(report: { day?: string; from?: string; to?: string }): boolean {
    return report.day === undefined && !(report.from && report.to);
}

/**
 * Resolves weekly selected reports from the report map. Ignores stale daily/range
 * entries left in prev when switching back from daily mode.
 */
export function resolveWeeklySelectedReports<T extends LiceReportMapKeySource>(
    prev: T[],
    weekStringKeysInOrder: string[],
    map: Map<string, T>
): T[] {
    if (weekStringKeysInOrder.length === 0) {
        return [];
    }

    const weeklyPrevKeys = prev
        .filter((report): report is T => report != null && isWeeklyLiceReport(report))
        .map((report) => `${report.isoWeek}-${report.isoYear}`)
        .filter((key) => weekStringKeysInOrder.includes(key));

    const keysToUse = weeklyPrevKeys.length > 0 ? weeklyPrevKeys : [weekStringKeysInOrder[0]];

    return keysToUse
        .map((key) => map.get(key))
        .filter((report): report is T => report !== undefined);
}
